import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { StatusBadge } from "@/components/consolidation/DiagnosticsStatusBadge";

const SEV_TONE = { blocking: "text-red-700", warning: "text-amber-700", info: "text-sky-700" };
const SEV_LABEL = { blocking: "Bloccante", warning: "Attenzione", info: "Info" };
const EMP_STATUS = { to_verify: "to_verify", ready: "ready", baseline_approved: "baseline_approved", not_required: "not_configured" };

const MonthsTable = ({ e, onlyRelevant }) => {
  const rows = onlyRelevant ? e.months.filter((m) => m.blocking || m.severity !== "info") : e.months;
  return (
    <table className="w-full text-[11px]" data-testid={`diag-months-${e.employee_id}`}>
      <thead className="text-suite-neutral-500"><tr>{["Mese", "Cat.", "Cartellino", "Giorni presenza", "Chiuso", "Gravità", "Motivo", "Azione suggerita (non eseguita)"].map((h) => <th key={h} className="px-2 py-1 text-left font-medium">{h}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={8} className="px-2 py-2 text-suite-neutral-500">Nessun mese da verificare.</td></tr>}
        {rows.map((m) => (
          <tr key={m.label} className="border-t border-suite-neutral-100" data-testid={`diag-month-${e.employee_id}-${m.year}-${m.month}`}>
            <td className="px-2 py-1">{m.label}</td><td className="px-2 py-1 font-semibold">{m.category}</td>
            <td className="px-2 py-1">{m.cartellino}{m.reopened ? " (riaperto)" : ""}</td><td className="px-2 py-1">{m.attendance_days}</td>
            <td className="px-2 py-1">{m.closed ? (m.counters_snapshot ? "Sì · con fotografia" : "Sì · storico") : "No"}</td>
            <td className={`px-2 py-1 font-medium ${SEV_TONE[m.severity]}`}>{SEV_LABEL[m.severity]}</td>
            <td className="px-2 py-1">{m.reason}</td><td className="px-2 py-1">{m.action || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const EmployeeRow = ({ e, onlyRelevant }) => {
  const [open, setOpen] = useState(e.status === "to_verify");
  return (
    <div className="border-t border-suite-neutral-100" data-testid={`diag-employee-${e.employee_id}`}>
      <button className="w-full flex flex-wrap items-center gap-3 px-3 py-2 text-left text-xs hover:bg-suite-neutral-50" onClick={() => setOpen(!open)} data-testid={`diag-employee-toggle-${e.employee_id}`}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="font-medium min-w-40">{e.name}</span>
        <StatusBadge status={EMP_STATUS[e.status]} label={e.status_label} testId={`diag-employee-status-${e.employee_id}`} />
        <span>Analisi: {e.analysis_from || "—"} → {e.analysis_to || "—"}</span>
        <span>Ultimo chiuso: <b>{e.last_closed_month || "—"}</b></span>
        <span>Candidato baseline: <b data-testid={`diag-candidate-${e.employee_id}`}>{e.baseline_candidate || "—"}</b></span>
        {e.candidate_without_gaps && <span className="text-suite-neutral-500">Ultimo chiuso senza discontinuità: {e.candidate_without_gaps}</span>}
      </button>
      {open && (
        <div className="px-6 pb-3 space-y-2">
          <div className="text-[11px] text-suite-neutral-600">Assunzione: {e.hire_date || "—"} · Cessazione: {e.cessation_date || "—"} · Primo mese gestito: {e.first_managed_month || "—"} · Baseline: {e.baseline.approved ? `approvata ${e.baseline.approved}` : e.baseline.proposed ? `proposta ${e.baseline.proposed}` : "assente"}</div>
          {e.historical_period && <div className="text-[11px] rounded bg-suite-neutral-50 px-2 py-1" data-testid={`diag-historical-${e.employee_id}`}><b>E</b> · {e.historical_period.from} – {e.historical_period.to} ({e.historical_period.months} mesi): {e.historical_period.reason}</div>}
          {e.issues.map((i) => <div key={i.code} className={`text-[11px] ${SEV_TONE[i.severity]}`} data-testid={`diag-issue-${e.employee_id}-${i.code}`}>[{SEV_LABEL[i.severity]} · {i.category === "CRONO" ? "Cronologia" : "F"}] {i.msg}{i.action ? ` — Azione suggerita: ${i.action}` : ""}</div>)}
          <MonthsTable e={e} onlyRelevant={onlyRelevant} />
        </div>
      )}
    </div>
  );
};

export function DiagnosticsCompanyDetail({ companyId }) {
  const [company, setCompany] = useState(null);
  const [onlyRelevant, setOnlyRelevant] = useState(true);
  useEffect(() => {
    setCompany(null);
    api.get(`/counters-consolidation/diagnostics/companies/${companyId}`).then(({ data }) => setCompany(data.companies[0])).catch((e) => toast.error(apiErr(e)));
  }, [companyId]);
  if (!company) return <div className="text-xs text-suite-neutral-500" data-testid="diag-detail-loading">Caricamento dettaglio…</div>;
  return (
    <div className="rounded-lg border border-suite-neutral-200 bg-white" data-testid="diag-company-detail">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-suite-neutral-50 text-xs">
        <span className="font-semibold">{company.name} · {company.employees} dipendenti</span>
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={onlyRelevant} onChange={(ev) => setOnlyRelevant(ev.target.checked)} data-testid="diag-only-relevant-toggle" />Mostra solo mesi bloccanti o di attenzione</label>
      </div>
      {company.employee_details.map((e) => <EmployeeRow key={e.employee_id} e={e} onlyRelevant={onlyRelevant} />)}
    </div>
  );
}
